import { useCallback, useEffect, useState } from "react";
import { api, type ApiPage } from "../lib/api";

type Args =
  | { mode: "owner"; projectId: string }
  | { mode: "share"; shareToken: string };

type State =
  | { status: "loading" }
  | { status: "ready"; pages: ApiPage[] }
  | { status: "error"; message: string };

// Page list + owner-side mutations. Mirrors the shape of useComments so
// ReviewSurface can wire both with the same mental model. Comment counts
// surface here too — call `reload()` after a comment-create resolves so the
// tab badges stay in sync (ReviewSurface does this).
export function usePages(args: Args) {
  const [state, setState] = useState<State>({ status: "loading" });

  const reload = useCallback(async () => {
    try {
      const res =
        args.mode === "owner"
          ? await api.listPagesByProject(args.projectId)
          : await api.listPagesByShare(args.shareToken);
      setState({ status: "ready", pages: res.pages });
    } catch (err) {
      setState({
        status: "error",
        message: (err as Error).message ?? "Failed to load pages",
      });
    }
  }, [args.mode, "projectId" in args ? args.projectId : args.shareToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void reload();
  }, [reload]);

  const create = useCallback(
    async (input: { name: string; path: string }) => {
      if (args.mode !== "owner") throw new Error("reviewer cannot add pages");
      const res = await api.createPage(args.projectId, input);
      setState((prev) =>
        prev.status === "ready"
          ? { status: "ready", pages: [...prev.pages, res.page] }
          : { status: "ready", pages: [res.page] },
      );
      return res.page;
    },
    [args],
  );

  const rename = useCallback(
    async (pageId: string, name: string) => {
      if (args.mode !== "owner") throw new Error("reviewer cannot rename pages");
      const res = await api.renamePage(args.projectId, pageId, name);
      setState((prev) =>
        prev.status === "ready"
          ? {
              status: "ready",
              pages: prev.pages.map((p) => (p.id === pageId ? res.page : p)),
            }
          : prev,
      );
    },
    [args],
  );

  const remove = useCallback(
    async (pageId: string) => {
      if (args.mode !== "owner") throw new Error("reviewer cannot delete pages");
      await api.deletePage(args.projectId, pageId);
      setState((prev) =>
        prev.status === "ready"
          ? { status: "ready", pages: prev.pages.filter((p) => p.id !== pageId) }
          : prev,
      );
    },
    [args],
  );

  const setVisibility = useCallback(
    async (pageId: string, hidden: boolean) => {
      if (args.mode !== "owner")
        throw new Error("reviewer cannot toggle page visibility");
      const res = await api.setPageVisibility(args.projectId, pageId, hidden);
      setState((prev) =>
        prev.status === "ready"
          ? {
              status: "ready",
              pages: prev.pages.map((p) => (p.id === pageId ? res.page : p)),
            }
          : prev,
      );
    },
    [args],
  );

  const reorder = useCallback(
    async (pageIds: string[]) => {
      if (args.mode !== "owner") throw new Error("reviewer cannot reorder pages");

      let snapshot: ApiPage[] = [];
      setState((prev) => {
        if (prev.status !== "ready") return prev;
        snapshot = prev.pages;
        const byId = new Map(prev.pages.map((p) => [p.id, p]));
        const next: ApiPage[] = [];
        for (let i = 0; i < pageIds.length; i++) {
          const id = pageIds[i];
          if (!id) continue;
          const p = byId.get(id);
          if (!p) continue;
          next.push({ ...p, orderIndex: i });
          byId.delete(id);
        }
        for (const leftover of byId.values()) next.push(leftover);
        return { status: "ready", pages: next };
      });

      try {
        const res = await api.reorderPages(args.projectId, pageIds);
        setState({ status: "ready", pages: res.pages });
      } catch (err) {
        setState({ status: "ready", pages: snapshot });
        throw err;
      }
    },
    [args],
  );

  const pages = state.status === "ready" ? state.pages : [];
  return { state, pages, reload, create, rename, remove, setVisibility, reorder };
}
